import * as React from "react";
import { useTranslation } from "react-i18next";
import { Sidebar, type SidebarSection } from "@/components/vroom/sidebar";
import { AdminTopbar } from "@/components/vroom/admin-topbar";
import { MobileTopbar } from "@/components/vroom/mobile-topbar";
import { BottomNav } from "@/components/vroom/bottom-nav";
import { FAB } from "@/components/vroom/fab";
import { useProfileDialog } from "@/components/profile-dialog/profile-dialog-provider";
import type { RoleKind } from "@/components/vroom/role-pill";
import type { AppSection } from "@/lib/app-sidebar";

export interface AdminShellUser {
  firstName: string;
  lastName: string;
  role: RoleKind;
}

export interface AdminShellFab {
  onClick: () => void;
  label: string;
}

export interface AdminShellProps {
  user: AdminShellUser;
  section: AppSection;
  sidebarSections: SidebarSection[];
  breadcrumb?: React.ReactNode;
  bottomNavItems?: React.ReactNode;
  drawer?: React.ReactNode;
  topbarAction?: React.ReactNode;
  fab?: AdminShellFab;
  children: React.ReactNode;
}

export function AdminShell({
  user,
  section,
  sidebarSections,
  breadcrumb,
  bottomNavItems,
  drawer,
  topbarAction,
  fab,
  children,
}: AdminShellProps) {
  const { t } = useTranslation();
  const { openProfile } = useProfileDialog();
  const canSwitchSection = user.role === "super_admin" || user.role === "approver";
  const approvalLabel = user.role === "approver" ? t("common.nav.approval.label") : undefined;

  const navItemCount = React.Children.count(bottomNavItems);
  const showBottomNav = navItemCount > 1;
  const fabPosition: "center" | "right" =
    showBottomNav && section === "worker" && navItemCount % 2 === 1 ? "right" : "center";

  return (
    <div className="flex min-h-dvh bg-surface-2 text-ink-900">
      <div className="hidden md:flex">
        <Sidebar sections={sidebarSections} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="md:hidden">
          <MobileTopbar
            variant="admin"
            user={user}
            section={section}
            canSwitchSection={canSwitchSection}
            sectionSwitcherTargetLabel={approvalLabel}
            onAvatarClick={() => openProfile("account")}
          />
        </div>
        <div className="hidden md:block">
          <AdminTopbar
            user={user}
            breadcrumb={breadcrumb}
            action={topbarAction}
            onAvatarClick={() => openProfile("account")}
          />
        </div>
        <main className={`min-h-0 flex-1 bg-surface ${showBottomNav ? "pb-20 md:pb-0" : ""}`}>
          {children}
        </main>
        {drawer ? <div className="hidden md:block">{drawer}</div> : null}
        {showBottomNav ? (
          <div className="md:hidden">
            <BottomNav variant={section === "worker" ? "worker" : "admin"}>
              {bottomNavItems}
            </BottomNav>
          </div>
        ) : null}
        {fab ? (
          <div className="md:hidden">
            <FAB onClick={fab.onClick} ariaLabel={fab.label} position={fabPosition} />
          </div>
        ) : null}
      </div>
    </div>
  );
}
